import { useState } from "react";
import Heading from "../blocks/Heading";
import Input from "../blocks/Input";
import Snackbar from "../blocks/Snackbar";
import {
  hasValidationErrors,
  validateEmail,
  validatePassword,
  validatePasswordConfirm,
} from "../../utils/validation";
import { useAuth } from "../../context/AuthContext";
import Button from "../blocks/Button";
import VerificationCodeInput from "./Verify";
import {
  resetPassword,
  confirmResetPassword,
  type ResetPasswordOutput,
} from "aws-amplify/auth";

interface ForgotPasswordError {
  email: string | null;
  password: string | null;
  confirmPassword: string | null;
}

interface ForgotPasswordTouched {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
}

const ForgotPassword = () => {
  // Stage management
  const [stage, setStage] = useState(0); // 0: email, 1: code, 2: password

  // Form states
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Validation states
  const [touched, setTouched] = useState<ForgotPasswordTouched>({
    email: false,
    password: false,
    confirmPassword: false,
  });

  const [errors, setErrors] = useState<ForgotPasswordError>({
    email: null,
    password: null,
    confirmPassword: null,
  });

  // Loading and error handling
  const { isLoading, error: authError, clearError } = useAuth();
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (touched.email) {
      const emailError = validateEmail(value);
      setErrors((prev) => ({ ...prev, email: emailError }));
    }
  };

  const handleEmailBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    const emailError = validateEmail(email);
    setErrors((prev) => ({ ...prev, email: emailError }));
  };

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setError(null);

    // Validate email
    const emailError = validateEmail(email);
    setErrors((prev) => ({ ...prev, email: emailError }));

    if (emailError) {
      return;
    }

    try {
      setIsRateLimited(true);

      // Call AWS Amplify resetPassword
      const output: ResetPasswordOutput = await resetPassword({
        username: email,
      });

      // Check the next step
      if (
        output.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE"
      ) {
        setStage(1);
      } else if (output.nextStep.resetPasswordStep === "DONE") {
        setError("Password reset completed. Redirecting to login...");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }
    } catch (err) {
      const authErr = err as any;
      let errorMessage = "Failed to send reset code";

      if (authErr?.name === "UserNotFoundException") {
        errorMessage =
          "No account found with this email. Please check and try again.";
      } else if (authErr?.name === "InvalidParameterException") {
        errorMessage = "Invalid email address. Please check and try again.";
      } else if (authErr?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later.";
        setIsRateLimited(true);
      } else if (authErr?.name === "NotAuthorizedException") {
        errorMessage = "This user account cannot request a password reset.";
      } else if (authErr?.message) {
        errorMessage = authErr.message;
      }

      setError(errorMessage);
      console.error("Reset password error:", authErr);
    } finally {
      setIsRateLimited(false);
    }
  };

  // ============================================================================
  // STAGE 1: Code Verification
  // ============================================================================

  const handleVerifyCode = async (code: string) => {
    setVerificationCode(code);
    setStage(2);
  };

  const handleResendResetCode = async () => {
    clearError();
    setError(null);

    try {
      setIsRateLimited(true);

      // Call AWS Amplify resetPassword again to resend
      await resetPassword({
        username: email,
      });

      // Code resent successfully - cooldown will be handled by VerificationCodeInput
    } catch (err) {
      const authErr = err as any;
      let errorMessage = "Failed to resend reset code";

      if (authErr?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (authErr?.message) {
        errorMessage = authErr.message;
      }

      throw new Error(errorMessage);
    } finally {
      setIsRateLimited(false);
    }
  };

  // ============================================================================
  // STAGE 2: New Password Entry
  // ============================================================================

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);

    if (touched.password) {
      const passwordError = validatePassword(value);
      setErrors((prev) => ({ ...prev, password: passwordError }));
    }
  };

  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    const passwordError = validatePassword(password);
    setErrors((prev) => ({ ...prev, password: passwordError }));
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (touched.confirmPassword) {
      const confirmError = validatePasswordConfirm(password, value);
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    const confirmError = validatePasswordConfirm(password, confirmPassword);
    setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setError(null);

    // Validate both passwords
    const passwordError = validatePassword(password);
    const confirmError = validatePasswordConfirm(password, confirmPassword);

    setErrors((prev) => ({
      ...prev,
      password: passwordError,
      confirmPassword: confirmError,
    }));

    if (passwordError || confirmError) {
      return;
    }

    try {
      setIsRateLimited(true);

      // Call AWS Amplify confirmResetPassword
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: password,
      });

      setError("Password reset successful! Redirecting to login...");

      // Clear form
      setEmail("");
      setVerificationCode("");
      setPassword("");
      setConfirmPassword("");
      setStage(0);

      // Redirect to login after a short delay
      setTimeout(() => {
        window.location.href = "/auth/login";
      }, 1000);
    } catch (err) {
      const authErr = err as any;
      let errorMessage = "Password reset failed";

      if (authErr?.name === "InvalidPasswordException") {
        errorMessage =
          "New password does not meet requirements. Must include uppercase, lowercase, numbers, and special characters.";
      } else if (authErr?.name === "CodeMismatchException") {
        errorMessage = "Invalid verification code. Please try again.";
      } else if (authErr?.name === "ExpiredCodeException") {
        errorMessage = "Verification code expired. Please start over.";
        setStage(0);
      } else if (authErr?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (authErr?.name === "UserNotFoundException") {
        errorMessage = "User not found. Please try again.";
        setStage(0);
      } else if (authErr?.message) {
        errorMessage = authErr.message;
      }

      setError(errorMessage);
      console.error("Reset password error:", authErr);
    } finally {
      setIsRateLimited(false);
    }
  };

  // ============================================================================
  // RENDER STAGES
  // ============================================================================

  // Stage 0: Email Entry
  if (stage === 0) {
    return (
      <div className="authentication">
        <Heading
          heading="Forgot password?"
          text="Enter your registered email address. We will send you a code to reset your password."
        />

        {error && (
          <Snackbar text={error} cleanErrorTimeout={() => setError(null)} />
        )}

        {authError && (
          <Snackbar text={authError} cleanErrorTimeout={clearError} />
        )}

        <form
          onSubmit={handleSendResetCode}
          className="authentication"
          noValidate
        >
          <Input
            id="email-input"
            name="Email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            error={errors.email || undefined}
            isValid={!errors.email && email.length > 0}
            disabled={isLoading || isRateLimited}
            required
          />

          <Button
            disabled={
              isLoading ||
              hasValidationErrors({
                email: errors.email,
              }) ||
              !email
            }
            text={isLoading ? "Sending reset code..." : "Send reset code"}
          />
        </form>
      </div>
    );
  }

  // Stage 1: Code Verification (Using Reusable Component)
  if (stage === 1) {
    return (
      <VerificationCodeInput
        email={email}
        heading="Verify your email"
        description={
          <>
            We’ve sent a security code to your registered e-mail address (
            {email}).
            <br />
            Please open your inbox and enter the security code to reset your
            account password.
            <br />
            <br />
            Check your spam or promotions folder – sometimes it hides there.
            <br />
            Still nothing? You can resend the code below.
          </>
        }
        onVerify={handleVerifyCode}
        onResend={handleResendResetCode}
        isLoading={isLoading}
        showResendButton={true}
      />
    );
  }

  // Stage 2: New Password Entry
  if (stage === 2) {
    return (
      <div className="authentication">
        <Heading
          heading="Set new password"
          text="Create a strong password with at least 8 characters including uppercase, lowercase, numbers, and special characters."
        />

        {error && (
          <Snackbar text={error} cleanErrorTimeout={() => setError(null)} />
        )}

        {authError && (
          <Snackbar text={authError} cleanErrorTimeout={clearError} />
        )}

        <form
          onSubmit={handleResetPassword}
          className="authentication"
          noValidate
        >
          <Input
            id="password-input"
            name="New Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            error={errors.password || undefined}
            isValid={!errors.password && password.length > 0}
            disabled={isLoading || isRateLimited}
            required
          />

          <Input
            id="confirm-password-input"
            name="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onBlur={handleConfirmPasswordBlur}
            error={errors.confirmPassword || undefined}
            isValid={!errors.confirmPassword && confirmPassword.length > 0}
            disabled={isLoading || isRateLimited}
            required
          />

          <div className="form__actions">
            <Button
              disabled={
                isLoading ||
                hasValidationErrors({
                  password: errors.password,
                  confirmPassword: errors.confirmPassword,
                }) ||
                !password ||
                !confirmPassword
              }
              text={isLoading ? "Saving changes..." : "Save changes"}
            />
          </div>
        </form>
      </div>
    );
  }

  return null;
};

export default ForgotPassword;
